<template>
  <modal modal-class="free-upgrade-modal" :anchor="anchor" :hide-header="true" :hide-footer="true" @hidden="clearRoute">
    <div class="d-flex justify-content-center align-items-stretch flex-column" data-testid="free-upgrade-modal">
      <div class="d-flex justify-content-center align-items-center">
        <span class="masthead">Get more with</span>
        <pro-badge
          utm_campaign="free-upgrade-prompt"
          utm_medium="web"
          :utm_source="page"
          :trial="trial"
          :visible="true"
          :learnmore_url="checkoutLink"
        ></pro-badge>
      </div>

      <div v-if="trial" class="byline">
        <p>
          Try pro <span class="orange-text">free for 7 days</span> and get great features like:
        </p>
      </div>
      <div v-else class="byline">
        <p>
          Upgrade to a Pro account and unlock features like:
        </p>
      </div>

      <div class="d-flex justify-content-between">
        <div v-for="item in items" :key="item.text" class="d-flex align-items-center flex-column">
          <img :src="item.image" class="illustration">
          <span class="item-text">{{ item.text }}</span>
        </div>
      </div>

      <a
        :href="checkoutLink"
        title="Upgrade Plan"
        class="btn button-frame d-flex align-items-center justify-content-center"
        data-testid="pro-btn-free-upgrade-modal"
      >
        <span class="label">
          {{ trial ? "Start free trial" : "Upgrade" }}
        </span>
      </a>

      <a href="/pricing" class="pricing-link">View all plans</a>
    </div>
  </modal>
</template>

<script>
import AnalyticsImage from '@img/analytics.svg';
import InfiniteImage from '@img/infinite.svg';
import BrandingImage from '@img/branding.svg';
import ProBadge from '@/src/components/pro_badge.vue';
import Modal from '@/src/components/modal.vue';
import track from '@/src/tracking';
import featureKey from '../../feature_key.macro';
import { mapState } from 'vuex';

export default {
  components: { Modal, ProBadge },
  data: function() {
    let page = this.$route.path.includes("dashboard") ? "dashboard" : "viewer";
    return {
      page: page,
      anchor: "#free-upgrade",
      items: [
        {
          image: InfiniteImage,
          text: "Unlimited Recording",
        },
        {
          image: AnalyticsImage,
          text: "Advanced Analytics"
        },
        {
          image: BrandingImage,
          text: "Custom Branding"
        }
      ],
      checkoutLink: `/purchase?plan=pro&trial=${this.trial}&onboarding=false&utm_campaign=in_product_prompt_${page}`
    }
  },
  methods: {
    clearRoute: function() {
      this.$router.push({ path: this.$route.path, hash: null });
    },
    trackShown: function() {
      track("upgrade_modal_shown", {
        campaign: "rev-12",
        source: this.page,
        goal: this.trial ? "trial" : "purchase"
      });
    }
  },
  computed: {
    ...mapState({
      trial: state => state.features?.[featureKey("free-upgrade-prompt")]
    })
  },
  watch: {
    trial: function(newVal, _oldVal) {
      // Make sure launch darkly experiment is defined
      const experimentSet = typeof newVal === "boolean";
      if (gon.free_upgrade_prompt && experimentSet && this.$route.hash !== this.anchor) {
        this.$router.push({ path: this.$route.path, hash: this.anchor });
        this.trackShown();
      }
    }
  }
}
</script>

<!--
  Not scoped because we cannot pass in scoped classes to vue-bootstrap
  from grandparent components when they are passed in via class directives.
  ex: content-class, modal-class, etc.
-->
<style lang="scss">
@import '@/packs/application.scss';

html .free-upgrade-modal {
  font-family: SFPro;

  .modal-body {
    padding: 0;
  }

  .modal-dialog {
    top: 30%;

    @media screen and (max-width: 500px) {
      top: 15%;
    }
  }

  .modal-content {
    max-width: 32rem;
    border-radius: 16px;
    box-shadow: 0 5px 10px 0 rgba(0, 0, 0, 0.08);
    border: solid 0.5px #b5b5b5;
    background-color: $white;
    padding: 2.313rem 3.438rem 1.688rem 3.438rem;

    @media screen and (max-width: 500px) {
      padding: 1rem;
    }
  }
}
</style>

<style lang="scss" scoped>
@import '@/packs/application.scss';

.free-upgrade-modal .modal-content {
  .masthead {
    font-size: 2rem;
    font-weight: 600;
    color: $black;

    @media screen and (max-width: 500px) {
      font-size: 1.5rem;
    }
  }

  .pro-badge {
    color: white;
    text-align: center;
    font-weight: 900;
    margin-left: 10px;
    font-size: 1.5rem;
    width: 4.938rem;
    height: 2.375rem;
    border-radius: 288px;
    border: solid white 2px;
    box-shadow: 0 2px 7px 2px rgba(0, 0, 0, 0.21);
    background-color: $white;
  }

  .badges-pro-shadow {
    max-width: 6.063rem;
    max-height: 3.5rem;
    object-fit: contain;
  }

  .byline {
    font-size: 1rem;
    color: #333333;
    margin: .438rem 0 2.063rem 0;
    display: flex;
    justify-content: center;

    p {
      padding: 0;
      margin: 0;
    }

    .orange-text {
      font-weight: 600;
      color: #e87d2b;
    }
  }

  .illustration {
    object-fit: contain;
  }

  .item-text {
    max-width: 6.375rem;
    max-height: 2.875rem;
    margin: 1rem 0 0;
    font-size: 1.25rem;
    font-weight: 900;
    text-align: center;
    color: #333333;

    @media screen and (max-width: 500px) {
      font-size: 1rem;
    }
  }

  .button-frame {
    max-width: 25.125rem;
    max-height: 3rem;
    margin: 3.313rem 0 1.25rem;
    border-radius: 5px;
    background-image: linear-gradient(96deg, #f09819 14%, #ff5858 100%);

    .label {
      font-size: 1rem;
      text-align: center;
      color: $white;
    }
  }

  .pricing-link {
    font-size: 1rem;
    text-align: center;
    color: $bright-blue;
    text-decoration: none;
  }
}
</style>
